/**
 * Gates a route behind authentication and, optionally, a role.
 *
 * Redirects rather than rendering an error: an unauthenticated visitor asking
 * for a page wants to sign in and be taken there, so the attempted location is
 * carried in the redirect state.
 *
 * This is interface convenience. PRD §6.2 puts authorisation on the server —
 * a route guard removed from the bundle changes nothing about what the API
 * will answer.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.8, PRD §6.2
 */
import { Navigate, Outlet, useLocation } from 'react-router';

import type { Role } from '@nexus/contracts';

import { useAuth } from '../lib/auth-context';

export interface RequireAuthProps {
  /** Minimum role. Omit to require only that the visitor is signed in. */
  role?: Role;
}

export function RequireAuth({ role }: RequireAuthProps): React.JSX.Element | null {
  const { user, isLoading, can } = useAuth();
  const location = useLocation();

  /* Nothing is rendered while the session is being restored: showing the login
     form first would make a returning user with a valid cookie see a flash of
     it before being redirected away. */
  if (isLoading) return null;

  if (user === null) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  /* Signed in but not permitted: back to the dashboard rather than to login,
     which would suggest signing in as someone else would help. */
  if (role !== undefined && !can(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
